import { useId } from 'react';

export interface WaylineChatIconProps {
  size?: number;
  className?: string;
}

export function WaylineChatIcon({
  size = 24,
  className,
}: WaylineChatIconProps): JSX.Element {
  const iconId = useId().replace(/:/g, '');
  const gradientId = `wayline-chat-gradient-${iconId}`;
  const lineGlowId = `wayline-chat-line-glow-${iconId}`;
  const nodeGlowId = `wayline-chat-node-glow-${iconId}`;
  const sparkGradientId = `wayline-chat-spark-${iconId}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="120 235 1010 640"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="166"
          y1="854"
          x2="1093"
          y2="299"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#4DEBFF" />
          <stop offset="0.52" stopColor="#3C82FF" />
          <stop offset="1" stopColor="#A855F7" />
        </linearGradient>
        <linearGradient
          id={sparkGradientId}
          x1="905"
          y1="236"
          x2="1038"
          y2="346"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#9BF8FF" />
          <stop offset="0.55" stopColor="#56A8FF" />
          <stop offset="1" stopColor="#C084FC" />
        </linearGradient>
        <filter id={lineGlowId} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="9" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={nodeGlowId} x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="14" result="blurOuter" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="5.5" result="blurInner" />
          <feMerge>
            <feMergeNode in="blurOuter" />
            <feMergeNode in="blurInner" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g
        stroke={`url(#${gradientId})`}
        strokeWidth="18"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#${lineGlowId})`}
        opacity="0.96"
      >
        <path d="M472.5 853.5L382.5 695M472.5 853.5L581 686.5M472.5 853.5L433.25 608.5M382.5 695L433.25 608.5M382.5 695L581 686.5M581 686.5L484 522L433.25 608.5M581 686.5L433.25 608.5M371 672L472.5 506M371 672L363 485.5M371 672L268.75 485.5M472.5 506L355 299M472.5 506L363 485.5M355 299H166.5M355 299L363 485.5M166.5 299L363 485.5M166.5 299L268.75 485.5M496.5 506L589.5 672L627 602M496.5 506L627 299M496.5 506L695.5 402.5M496.5 506L627 602M496.5 506H764M627 602L669.5 672L764 506M627 602L764 506M627 602V299M764 506L695.5 402.5M627 299L695.5 402.5M363 485.5H268.75" />
        <path d="M787.256 854L678.847 686.849M787.256 854L778.91 613M787.256 854L832.218 774.679M877.181 695.357L775.766 522.201M877.181 695.357L832.218 774.679M877.181 695.357L778.91 613M775.766 522.201L678.847 686.849M775.766 522.201L778.91 613M678.847 686.849L778.91 613M678.847 686.849L832.218 774.679M787.256 506.187L888.671 672.336L939.753 579.002M787.256 506.187L904.658 299M787.256 506.187L990.836 485.668M787.256 506.187L939.753 579.002M787.256 506.187L1093 299M904.658 299H1093M904.658 299L990.836 485.668M1093 299L990.836 485.668M990.836 485.668L939.753 579.002" />
      </g>

      <g fill={`url(#${gradientId})`} opacity="0.58" filter={`url(#${nodeGlowId})`}>
        <circle cx="472.5" cy="853.5" r="18" />
        <circle cx="382.5" cy="695" r="15" />
        <circle cx="581" cy="686.5" r="15" />
        <circle cx="433.25" cy="608.5" r="13" />
        <circle cx="484" cy="522" r="13" />
        <circle cx="371" cy="672" r="15" />
        <circle cx="472.5" cy="506" r="15" />
        <circle cx="363" cy="485.5" r="13" />
        <circle cx="268.75" cy="485.5" r="13" />
        <circle cx="355" cy="299" r="15" />
        <circle cx="166.5" cy="299" r="18" />
        <circle cx="496.5" cy="506" r="15" />
        <circle cx="589.5" cy="672" r="13" />
        <circle cx="627" cy="602" r="15" />
        <circle cx="627" cy="299" r="18" />
        <circle cx="695.5" cy="402.5" r="13" />
        <circle cx="669.5" cy="672" r="13" />
        <circle cx="764" cy="506" r="15" />
        <circle cx="787.256" cy="854" r="18" />
        <circle cx="678.847" cy="686.849" r="15" />
        <circle cx="778.91" cy="613" r="13" />
        <circle cx="832.218" cy="774.679" r="15" />
        <circle cx="877.181" cy="695.357" r="15" />
        <circle cx="775.766" cy="522.201" r="13" />
        <circle cx="787.256" cy="506.187" r="15" />
        <circle cx="888.671" cy="672.336" r="13" />
        <circle cx="939.753" cy="579.002" r="13" />
        <circle cx="904.658" cy="299" r="15" />
        <circle cx="990.836" cy="485.668" r="13" />
        <circle cx="1093" cy="299" r="18" />
      </g>

      <g fill="#D8F8FF" opacity="0.96">
        <circle cx="472.5" cy="853.5" r="7" />
        <circle cx="382.5" cy="695" r="6" />
        <circle cx="581" cy="686.5" r="6" />
        <circle cx="433.25" cy="608.5" r="5" />
        <circle cx="484" cy="522" r="5" />
        <circle cx="371" cy="672" r="6" />
        <circle cx="472.5" cy="506" r="6" />
        <circle cx="363" cy="485.5" r="5" />
        <circle cx="268.75" cy="485.5" r="5" />
        <circle cx="355" cy="299" r="6" />
        <circle cx="166.5" cy="299" r="7" />
        <circle cx="496.5" cy="506" r="6" />
        <circle cx="589.5" cy="672" r="5" />
        <circle cx="627" cy="602" r="6" />
        <circle cx="627" cy="299" r="7" />
        <circle cx="695.5" cy="402.5" r="5" />
        <circle cx="669.5" cy="672" r="5" />
        <circle cx="764" cy="506" r="6" />
        <circle cx="787.256" cy="854" r="7" />
        <circle cx="678.847" cy="686.849" r="6" />
        <circle cx="778.91" cy="613" r="5" />
        <circle cx="832.218" cy="774.679" r="6" />
        <circle cx="877.181" cy="695.357" r="6" />
        <circle cx="775.766" cy="522.201" r="5" />
        <circle cx="787.256" cy="506.187" r="6" />
        <circle cx="888.671" cy="672.336" r="5" />
        <circle cx="939.753" cy="579.002" r="5" />
        <circle cx="904.658" cy="299" r="6" />
        <circle cx="990.836" cy="485.668" r="5" />
        <circle cx="1093" cy="299" r="7" />
      </g>

      <g filter={`url(#${nodeGlowId})`}>
        <path
          d="M975 238L988 282L1032 295L988 308L975 352L962 308L918 295L962 282Z"
          fill={`url(#${sparkGradientId})`}
        />
        <path
          d="M902 269L908 287L926 293L908 299L902 317L896 299L878 293L896 287Z"
          fill="#A6F7FF"
        />
      </g>
    </svg>
  );
}
